import { Company } from 'module/api/company/types';
import { useTranslate } from 'module/hooks/use-translation';
import { Dropdown } from 'module/ui/components/atoms/dropdown/dropdown';
import { Menu } from 'module/ui/components/atoms/dropdown/menu';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Loading } from 'module/ui/components/atoms/loading';
import {
  ProductiveHeading01,
  ProductiveHeading03,
} from 'module/ui/ozone/atoms';
import {
  faChevronDown,
  faAlignLeft,
  faCalendar,
} from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { useSearchParams } from '@rocketlane/rocket-router';
import { SignalType } from 'api/signal/types';
import moment from 'moment';
import { useAccountMetaQuery } from 'queries/account';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useState,
  useMemo,
  Fragment,
} from 'react';
import styled from 'styled-components';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { TimelineSignalCard } from 'components/organisms/signal-output/timeline-signal-card';
import { KryptonMenuIcon } from 'components/organisms/timeline-details';
import { getStoredGroupBy, saveGroupBy } from './group-by-utils';
import { MeetingsEmailsTOC } from './meetings-emails-toc';
import { MonthTOC } from './month-toc';
import {
  getSignalsV2CanCreateSignal,
  SignalsV2AccountEmptyState,
  type SignalsV2SignalPermissions,
} from './signals-v2-account-empty-state';
import { getPeriodKeyForDate, useTimelineData } from './use-timeline-data';
import {
  GroupByType,
  getPeriodStart,
  SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX,
} from './utils';

const MEETINGS_AND_EMAILS_FLAG_KEY = 'signals-timeline-meetings-emails-flag';

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
  position: relative;
  justify-content: flex-start;
  align-self: flex-start;
  overflow-y: auto;
  /* Offset for sticky period labels so they sit below the sticky group-by header */
  --signals-v2-period-sticky-top: 56px;
`;

const TocSidebar = styled.div`
  position: sticky;
  left: var(--spacing-04);
  padding: var(--spacing-04);
  z-index: 10;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  display: flex;
  height: 100%;
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0 var(--spacing-08);
  position: relative;
  gap: 0;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding: var(--spacing-03) 0;
  position: sticky;
  top: 0;
  background-color: var(--background-primary);
  z-index: 5;
  margin-bottom: var(--spacing-06);
  box-shadow: 0px 5px 10px 0px #ffffff;
`;

const GroupByDropdownTrigger = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: var(--spacing-03);
  border-radius: var(--border-radius-default);
  background-color: var(--scarlet-gray-30);
  color: var(--text-primary);
  margin-right: var(--spacing-06);
  cursor: pointer;
  font-size: var(--font-size-base);
  font-weight: 500;
  line-height: var(--line-height-tag);
  border: none;
  transition: all 0.2s ease;
  height: 32px;

  &:hover {
    background-color: var(--gray-20);
  }
`;

const GroupByLabel = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: var(--font-size-base);
`;

/** Fixed width so the overlay does not resize when the trigger label length changes */
const GROUP_BY_MENU_WIDTH_PX = 280;

const StyledMenu = styled(Menu)`
  width: 100%;
  min-width: 0;
  background: var(--white-100);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: var(--border-radius-default);
  padding: var(--spacing-02) 0;

  .ant-dropdown-menu-item-group-title {
    padding: var(--spacing-02) var(--spacing-05);
    font-size: var(--font-size-sm);
    font-weight: 600;
    color: var(--text-secondary);
    text-transform: none;
  }

  .ant-dropdown-menu-item {
    padding: var(--spacing-03) var(--spacing-05);
    font-size: var(--font-size-sm);
    color: var(--text-primary);
  }

  .ant-dropdown-menu-item:hover {
    background-color: var(--cell-hover);
  }

  .ant-dropdown-menu-item-selected {
    background-color: var(--cell-selected-background);
    color: var(--text-primary);
  }
`;

const PeriodSection = styled.div<{ $isMeetingsAndEmails?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  justify-content: center;
  gap: ${(p) =>
    p.$isMeetingsAndEmails ? 'var(--spacing-05)' : 'var(--spacing-04)'};
  overflow: visible;
  width: 100%;
  padding-left: 0;
  padding-bottom: var(--spacing-06);
  border-bottom: 1px solid var(--gray-10);
`;

/** Stretches to full period height so the sticky label can pin until the period scrolls away */
const PeriodLabelColumn = styled.div`
  flex-shrink: 0;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const PeriodContentColumn = styled.div<{ $meetingsEmailsWithItems?: boolean }>`
  flex: ${(p) =>
    p.$meetingsEmailsWithItems
      ? '1 1 auto'
      : `0 1 ${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px`};
  max-width: ${(p) =>
    p.$meetingsEmailsWithItems
      ? 'none'
      : `${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px`};
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const Spacer = styled.div<{ height: number }>`
  height: ${(props) => props.height}px;
  flex-shrink: 0;
`;

const PeriodLabelLeft = styled.div`
  position: sticky;
  top: var(--signals-v2-period-sticky-top, 56px);
  align-self: flex-start;
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-40);
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
  width: 100%;
  max-width: 200px;
  text-align: right;
  padding-right: var(--spacing-04);
  z-index: 2;
  pointer-events: none;
  background-color: var(--background-primary);
  padding-top: var(--spacing-02);
  padding-bottom: var(--spacing-02);
  line-height: 1.4;
  overflow: visible;
`;

const TimelineItemDateLabel = styled.div`
  position: sticky;
  top: var(--signals-v2-period-sticky-top, 56px);
  align-self: flex-start;
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-40);
  white-space: nowrap;
  width: 100%;
  max-width: 200px;
  text-align: right;
  padding-right: var(--spacing-04);
  z-index: 2;
  pointer-events: none;
  background-color: var(--background-primary);
  padding-top: var(--spacing-02);
  padding-bottom: var(--spacing-02);
`;

/** Full-height column so the date label can stick for the whole meeting/email block */
const TimelineItemDateColumn = styled.div`
  flex-shrink: 0;
  width: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const TimelineItemContentColumn = styled.div<{
  $constrainReadingWidth?: boolean;
}>`
  flex: ${(p) =>
    p.$constrainReadingWidth
      ? `0 1 ${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px`
      : '1'};
  max-width: ${(p) =>
    p.$constrainReadingWidth
      ? `${SIGNALS_V2_MAIN_CONTENT_MAX_WIDTH_PX}px`
      : 'none'};
  min-width: 0;
  width: ${(p) => (p.$constrainReadingWidth ? '100%' : 'auto')};
  display: flex;
  flex-direction: column;
  gap: var(--spacing-05);
`;

const TimelineItemsContainer = styled.div<{
  $isMeetingsAndEmails?: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
  width: 100%;
`;

const TimelineItem = styled.div<{ $isMeetingsAndEmails?: boolean }>`
  display: flex;
  flex-direction: ${(p) => (p.$isMeetingsAndEmails ? 'row' : 'column')};
  align-items: stretch;
  gap: ${(p) =>
    p.$isMeetingsAndEmails ? 'var(--spacing-04)' : 'var(--spacing-05)'};
  padding-left: 0;
  position: relative;
  justify-content: ${(p) => (p.$isMeetingsAndEmails ? 'center' : 'flex-start')};

  &:last-child {
    border-bottom: none;
  }
`;

const SignalCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-07);
`;

const TimelineHeader = styled.div`
  margin-bottom: var(--spacing-05);
`;

const RecapSection = styled.div`
  margin-top: var(--spacing-05);
  margin-bottom: var(--spacing-05);
`;

const RecapTitle = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
  margin-bottom: var(--spacing-05);
`;

const RecapText = styled.p`
  color: var(--gray-100);
  font-size: var(--font-size-base);
  line-height: var(--line-height-05);
  margin-bottom: var(--spacing-05);
`;

const PeriodEmptyState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 12rem;
  color: var(--text-tertiary);
  font-size: 14px;
  padding: var(--spacing-06) var(--spacing-04);
  text-align: center;
`;

const FlexWrapperWithShortGapBaseline = styled(FlexWrapperWithShortGap)`
  align-items: baseline;
`;

const FullPageLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-primary);
  z-index: 1000;
  pointer-events: all;
  overflow: hidden;
`;

const TimelineContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  gap: var(--spacing-07);
`;

const TopLoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-08);
  min-height: 200px;
  position: sticky;
  top: 0;
  background-color: var(--background-primary);
  z-index: 5;
`;

const Sentinel = styled.div`
  height: 1px;
  width: 100%;
  pointer-events: none;
  visibility: hidden;
`;

const BottomLoadingIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: var(--spacing-08);
  min-height: 200px;
  position: sticky;
  bottom: 0;
  background-color: var(--background-primary);
  z-index: 5;
`;

interface SignalsV2TimelineViewProps {
  company: Company;
  projectId?: number;
  signalPermissions?: SignalsV2SignalPermissions;
}

const ITEMS_ABOVE = 5; // Top 5 elements
const ITEMS_BELOW = 10; // Bottom 10 elements
const ESTIMATED_ITEM_HEIGHT = 200; // Estimated height per period section

export const SignalsV2TimelineView: React.FC<SignalsV2TimelineViewProps> = ({
  company,
  projectId,
  signalPermissions,
}) => {
  const translate = useTranslate();
  const { data: signalConfigurations = [], isLoading: isSignalConfigsLoading } =
    useGetAllSignalConfigurationsQuery();
  const hasNoSignalConfigurations =
    !isSignalConfigsLoading && signalConfigurations.length === 0;
  const canCreateSignal = getSignalsV2CanCreateSignal(signalPermissions);

  // Check localStorage for meetings and emails flag
  const getMeetingsAndEmailsFlag = (): boolean => {
    try {
      const flag = localStorage.getItem(MEETINGS_AND_EMAILS_FLAG_KEY);

      return flag === 'true';
    } catch {
      return false;
    }
  };

  const [searchParams] = useSearchParams();
  const createdAtFromUrl = searchParams.get('createdAt');

  const [isMeetingsAndEmails, setIsMeetingsAndEmails] = useState<boolean>(
    getMeetingsAndEmailsFlag(),
  );

  // Use MONTH when meetings and emails is selected, otherwise use stored preference
  const [groupBy, setGroupBy] = useState<GroupByType>(
    createdAtFromUrl
      ? 'MONTH'
      : isMeetingsAndEmails
        ? 'MONTH'
        : getStoredGroupBy(),
  );

  const prevCreatedAtFromUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // test
    if (createdAtFromUrl) {
      setGroupBy('MONTH');
    } else if (prevCreatedAtFromUrlRef.current) {
      setGroupBy(getStoredGroupBy());
    }
    prevCreatedAtFromUrlRef.current = createdAtFromUrl;
  }, [createdAtFromUrl]);

  // Fetch billing start date from account meta
  const { data: accountMeta } = useAccountMetaQuery();
  const billingStartDate = useMemo(() => {
    if (accountMeta?.company?.createdAt) {
      // createdAt is a timestamp in milliseconds
      return moment(accountMeta.company.createdAt).format('YYYY-MM-DD');
    }

    // Fallback to default if not available
    return '2020-04-01';
  }, [accountMeta]);

  const {
    data,
    selectedPeriodKey,
    isInitializing,
    isLoadingSelectedPeriod,
    isLoadingDown,
    isLoadingUp,
    isLoadingTOC,
    handlePeriodSelect,
    loadEarlierPeriods,
    loadLaterPeriods,
    monthKeysWithData,
  } = useTimelineData({
    companyId: company.companyId,
    groupBy,
    billingStartDate,
    view: isMeetingsAndEmails ? 'MEETINGS&EMAILS' : undefined,
  });

  const hasJumpedForUrlRef = useRef<string | null>(null);
  const [hasJumpedForUrl, setHasJumpedForUrl] = useState(false);
  const [pendingScrollSignalId, setPendingScrollSignalId] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (
      !isInitializing &&
      createdAtFromUrl &&
      hasJumpedForUrlRef.current !== createdAtFromUrl
    ) {
      hasJumpedForUrlRef.current = createdAtFromUrl;
      setHasJumpedForUrl(true);
      handlePeriodSelect(getPeriodKeyForDate(createdAtFromUrl, 'MONTH'));

      const hash = window.location.hash;
      const match = hash.match(/signalOccurrenceId=(\d+)/);

      if (match) {
        setPendingScrollSignalId(parseInt(match[1], 10));
      }
    }
  }, [isInitializing, createdAtFromUrl, handlePeriodSelect]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [viewportIndex, setViewportIndex] = useState<number>(0);
  const [itemHeights, setItemHeights] = useState<Map<number, number>>(
    new Map(),
  );
  const [selectedTimelineId, setSelectedTimelineId] = useState<string>('');
  const [isLoadingTimeline, setIsLoadingTimeline] = useState<boolean>(false);
  const lastScrollTop = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);
  const itemRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);

  // Reset viewport state when groupBy or isMeetingsAndEmails changes
  useEffect(() => {
    // Force complete reset of all viewport-related state
    setViewportIndex(0);
    setItemHeights(new Map());
    itemRefs.current.clear();
    lastScrollTop.current = 0;
    isUserScrollingRef.current = false;
    isScrolling.current = false;
    previousViewportIndexRef.current = 0;
    previousSelectedPeriodKeyRef.current = '';
    setSelectedTimelineId('');
    previousSelectedTimelineIdRef.current = '';
    setIsLoadingTimeline(false);
    previousTimelineLoadingStateRef.current = false;

    // Clear any pending timeouts
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
    if (viewportIndexUpdateTimeoutRef.current) {
      clearTimeout(viewportIndexUpdateTimeoutRef.current);
      viewportIndexUpdateTimeoutRef.current = null;
    }

    // Reset scroll position
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
  }, [groupBy, isMeetingsAndEmails]);

  // Reset viewport index when data changes from empty to having data (after groupBy change)
  useEffect(() => {
    if (data.length > 0 && viewportIndex >= data.length) {
      setViewportIndex(0);
    }
  }, [data.length, viewportIndex]);

  const HIGHLIGHT_DURATION_MS = 5000;

  const [highlightedSignalOccurrenceId, setHighlightedSignalOccurrenceId] =
    useState<number | null>(null);
  const isScrollingToSignalRef = useRef(false);

  // Phase 1 (useLayoutEffect): when createdAt + hash are present in the URL,
  // align the virtual viewport to the target period so the card is rendered in the DOM.
  // This only handles viewport alignment — it does NOT scroll to the card or highlight.
  useLayoutEffect(() => {
    if (pendingScrollSignalId === null || data.length === 0) {
      return;
    }

    const targetPeriodIndex = data.findIndex((period) =>
      period.timelines.some((timeline) =>
        timeline.signalOccurrences?.some(
          (s) => s.signalOccurrenceId === pendingScrollSignalId,
        ),
      ),
    );

    if (targetPeriodIndex < 0) {
      return;
    }

    // Bring the target period into the virtual window
    if (viewportIndex !== targetPeriodIndex) {
      setViewportIndex(targetPeriodIndex);
    }
  }, [pendingScrollSignalId, viewportIndex, data]);

  // Phase 2 (useEffect): once the card is in the DOM, scroll to it and highlight.
  // Runs after paint so the card is guaranteed to be rendered.
  useEffect(() => {
    if (pendingScrollSignalId === null || data.length === 0) {
      return;
    }

    if (isScrollingToSignalRef.current) {
      return;
    }

    const targetId = pendingScrollSignalId;

    // Check if the signal is in the current data
    const signalInData = data.some((period) =>
      period.timelines.some((timeline) =>
        timeline.signalOccurrences?.some(
          (s) => s.signalOccurrenceId === targetId,
        ),
      ),
    );

    if (!signalInData) {
      return;
    }

    // Small delay to let the period-scroll effect and DOM settle
    const delayTimeoutId = window.setTimeout(() => {
      const card = containerRef.current?.querySelector(
        `[data-signal-occurrence-id="${targetId}"]`,
      ) as HTMLElement | null;

      if (card) {
        isScrollingToSignalRef.current = true;
        setPendingScrollSignalId(null);
        setHighlightedSignalOccurrenceId(targetId);
        card.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Reset scrolling flag after scroll settles
        window.setTimeout(() => {
          isScrollingToSignalRef.current = false;
        }, 800);
      }
    }, 400);

    const clearHighlightTimeoutId = window.setTimeout(() => {
      setHighlightedSignalOccurrenceId(null);
    }, 400 + HIGHLIGHT_DURATION_MS);

    return () => {
      clearTimeout(delayTimeoutId);
      clearTimeout(clearHighlightTimeoutId);
    };
  }, [pendingScrollSignalId, viewportIndex, data]);

  // Calculate visible range
  // If there are few items, render all of them. Otherwise use virtualized rendering
  const visibleRange = useMemo(() => {
    // If we have fewer items than the visible window, render all
    if (data.length <= ITEMS_ABOVE + ITEMS_BELOW + 1) {
      return { start: 0, end: data.length };
    }

    const start = Math.max(0, viewportIndex - ITEMS_ABOVE);
    const end = Math.min(data.length, viewportIndex + ITEMS_BELOW + 1);

    return { start, end };
  }, [viewportIndex, data.length]);

  // Calculate total height of items before visible range
  const topSpacerHeight = useMemo(() => {
    let height = 0;

    for (let i = 0; i < visibleRange.start; i++) {
      height += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
    }

    return height;
  }, [visibleRange.start, itemHeights]);

  // Calculate total height of items after visible range
  const bottomSpacerHeight = useMemo(() => {
    let height = 0;

    for (let i = visibleRange.end; i < data.length; i++) {
      height += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
    }

    return height;
  }, [visibleRange.end, data.length, itemHeights]);

  // Measure item heights
  useEffect(() => {
    const newHeights = new Map(itemHeights);
    let hasChanges = false;

    itemRefs.current.forEach((element, index) => {
      if (element) {
        const height = element.offsetHeight;
        const currentHeight = newHeights.get(index);

        if (currentHeight !== height) {
          newHeights.set(index, height);
          hasChanges = true;
        }
      }
    });

    if (hasChanges) {
      setItemHeights(new Map(newHeights));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, visibleRange]);

  // Handle scroll to update viewport index
  const handleScroll = useCallback(() => {
    if (!containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const currentScrollTop = scrollTop;

    // Determine scroll direction
    const scrollDelta = currentScrollTop - lastScrollTop.current;

    // Always update lastScrollTop to track scroll position accurately
    lastScrollTop.current = currentScrollTop;

    // Load more data when scrolling down near the end (earlier periods)
    // This should always run, regardless of isScrolling flag
    const distanceFromBottom = scrollHeight - currentScrollTop - clientHeight;

    if (scrollDelta > 0 && distanceFromBottom < 1000) {
      loadEarlierPeriods();
    }

    // Load more data when scrolling up near the start (later periods)
    if (scrollDelta < 0 && currentScrollTop < 1000) {
      loadLaterPeriods();
    }

    // Only update viewport index if not programmatically scrolling
    // Debounce viewport index calculation to prevent scroll glitches
    if (isScrolling.current) {
      return;
    }

    if (viewportIndexUpdateTimeoutRef.current) {
      clearTimeout(viewportIndexUpdateTimeoutRef.current);
    }

    // Calculate which item should be in viewport based on scroll position
    // Do this immediately for better responsiveness, but debounce the state update
    let accumulatedHeight = 0;
    let newViewportIndex = 0;

    for (let i = 0; i < data.length; i++) {
      const itemHeight = itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
      const itemTop = accumulatedHeight;
      const itemBottom = accumulatedHeight + itemHeight;

      // Check if viewport center (scrollTop + clientHeight/2) is within this item
      const viewportCenter = currentScrollTop + clientHeight / 2;

      if (viewportCenter >= itemTop && viewportCenter < itemBottom) {
        newViewportIndex = i;
        break;
      }

      // If we've passed the viewport center, this is the item
      if (viewportCenter < itemTop) {
        newViewportIndex = Math.max(0, i - 1);
        break;
      }

      accumulatedHeight += itemHeight;
    }

    // If we reached the end, use the last item
    if (
      newViewportIndex === 0 &&
      accumulatedHeight > 0 &&
      currentScrollTop > accumulatedHeight
    ) {
      newViewportIndex = data.length - 1;
    }

    // Update viewport index if it changed - debounce to prevent too many updates
    if (viewportIndexUpdateTimeoutRef.current) {
      clearTimeout(viewportIndexUpdateTimeoutRef.current);
    }

    viewportIndexUpdateTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current || isScrolling.current) {
        return;
      }

      // Recalculate to ensure we have the latest scroll position
      const latestScrollTop = containerRef.current.scrollTop;
      let latestAccumulatedHeight = 0;
      let latestViewportIndex = 0;

      for (let i = 0; i < data.length; i++) {
        const itemHeight = itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
        const itemTop = latestAccumulatedHeight;
        const itemBottom = latestAccumulatedHeight + itemHeight;
        const viewportCenter =
          latestScrollTop + containerRef.current.clientHeight / 2;

        if (viewportCenter >= itemTop && viewportCenter < itemBottom) {
          latestViewportIndex = i;
          break;
        }

        if (viewportCenter < itemTop) {
          latestViewportIndex = Math.max(0, i - 1);
          break;
        }

        latestAccumulatedHeight += itemHeight;
      }

      if (
        latestViewportIndex === 0 &&
        latestAccumulatedHeight > 0 &&
        latestScrollTop > latestAccumulatedHeight
      ) {
        latestViewportIndex = data.length - 1;
      }

      // Update viewport index if it changed
      if (Math.abs(latestViewportIndex - viewportIndex) > 0) {
        isUserScrollingRef.current = true;
        previousViewportIndexRef.current = viewportIndex;
        setViewportIndex(latestViewportIndex);

        // Reset flag after user stops scrolling (debounce)
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          isUserScrollingRef.current = false;
          scrollTimeoutRef.current = null;
        }, 150);
      }
    }, 50); // Reduced debounce time for better responsiveness
  }, [
    data.length,
    itemHeights,
    viewportIndex,
    loadEarlierPeriods,
    loadLaterPeriods,
  ]);

  // Setup Intersection Observer for loading more data
  useEffect(() => {
    const container = containerRef.current;
    const topSentinel = topSentinelRef.current;
    const bottomSentinel = bottomSentinelRef.current;

    if (!container || !topSentinel || !bottomSentinel) {
      return;
    }

    // Disconnect existing observer
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    // Create new Intersection Observer
    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Bottom sentinel visible - load earlier periods (scroll down)
            if (entry.target === bottomSentinel) {
              if (!isLoadingDown) {
                loadEarlierPeriods();
              }
            }
            // Top sentinel visible - load later periods (scroll up)
            else if (entry.target === topSentinel) {
              if (!isLoadingUp) {
                loadLaterPeriods();
              }
            }
          }
        });
      },
      {
        root: container,
        rootMargin: '200px', // Trigger when 200px away from viewport
        threshold: 0,
      },
    );

    // Observe both sentinels
    intersectionObserverRef.current.observe(topSentinel);
    intersectionObserverRef.current.observe(bottomSentinel);

    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
        intersectionObserverRef.current = null;
      }
    };
  }, [
    loadEarlierPeriods,
    loadLaterPeriods,
    isLoadingDown,
    isLoadingUp,
    data.length,
  ]);

  useEffect(() => {
    const container = containerRef.current;

    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      lastScrollTop.current = container.scrollTop;

      return () => {
        container.removeEventListener('scroll', handleScroll);
        // Clean up scroll timeouts
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        if (viewportIndexUpdateTimeoutRef.current) {
          clearTimeout(viewportIndexUpdateTimeoutRef.current);
        }
      };
    }
  }, [handleScroll]);

  // Track if viewport index change is from user scroll or programmatic
  const isUserScrollingRef = useRef<boolean>(false);
  const previousViewportIndexRef = useRef<number>(viewportIndex);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const viewportIndexUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track previous selected period key to detect actual changes
  const previousSelectedPeriodKeyRef = useRef<string>('');
  const previousLoadingStateRef = useRef<boolean>(false);
  const previousSelectedTimelineIdRef = useRef<string>('');
  const previousTimelineLoadingStateRef = useRef<boolean>(false);

  // Set selected period as first viewport element and scroll to it
  useEffect(() => {
    // Don't update viewport while loading
    if (isLoadingSelectedPeriod) {
      previousLoadingStateRef.current = true;

      return;
    }

    // Track when loading completes
    const justFinishedLoading = previousLoadingStateRef.current;

    previousLoadingStateRef.current = false;

    // Don't update if no period selected or no data
    if (!selectedPeriodKey || data.length === 0 || !containerRef.current) {
      if (selectedPeriodKey) {
        previousSelectedPeriodKeyRef.current = selectedPeriodKey;
      }

      return;
    }

    // Always scroll to selected period when:
    // 1. Period key changed (always scroll when selection changes)
    // 2. Just finished loading (only if the selected period is in the newly loaded data)
    const periodKeyChanged =
      selectedPeriodKey !== previousSelectedPeriodKeyRef.current;
    const shouldScroll = justFinishedLoading || periodKeyChanged;

    if (!shouldScroll) {
      return;
    }

    // Reset user scrolling flag when period selection changes to allow scrolling
    if (periodKeyChanged) {
      isUserScrollingRef.current = false;
      // Clear any pending scroll timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    } else if (isUserScrollingRef.current) {
      // Don't scroll if user is actively scrolling and period hasn't changed
      return;
    }

    const selectedIndex = data.findIndex(
      (item) => item.periodKey === selectedPeriodKey,
    );

    // Scroll to selected period and set it as viewport start
    if (selectedIndex >= 0) {
      // Update viewport index to show selected period as first visible item
      // Set scrolling flag to prevent scroll handler from interfering
      isScrolling.current = true;
      setViewportIndex(selectedIndex);
      previousViewportIndexRef.current = selectedIndex;
      previousSelectedPeriodKeyRef.current = selectedPeriodKey;

      // Function to scroll to the selected period
      const scrollToSelectedPeriod = () => {
        if (!containerRef.current) {
          return;
        }

        // Try to find element by data-section-id first (more reliable)
        // For month format (YYYY-MM), replace all dashes with spaces
        const sectionId = selectedPeriodKey.replace(/-/g, ' ');
        const targetElement = containerRef.current.querySelector(
          `[data-section-id="${sectionId}"]`,
        ) as HTMLElement;

        if (targetElement) {
          // Use scrollIntoView for more reliable scrolling
          isScrolling.current = true;

          // Calculate position relative to container
          const containerRect = containerRef.current.getBoundingClientRect();
          const elementRect = targetElement.getBoundingClientRect();
          const relativeTop =
            elementRect.top -
            containerRect.top +
            containerRef.current.scrollTop;

          // Scroll to position
          containerRef.current.scrollTop = relativeTop;
          lastScrollTop.current = relativeTop;

          // Reset scrolling flag after animation
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isScrolling.current = false;
            });
          });
        } else {
          // Fallback: Calculate scroll position using item heights
          let accumulatedHeight = 0;

          for (let i = 0; i < selectedIndex; i++) {
            accumulatedHeight += itemHeights.get(i) || ESTIMATED_ITEM_HEIGHT;
          }

          isScrolling.current = true;
          containerRef.current.scrollTop = accumulatedHeight;
          lastScrollTop.current = accumulatedHeight;

          // Reset scrolling flag after animation
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isScrolling.current = false;
            });
          });
        }
      };

      // Skip the scroll when a deep-link signal scroll is pending —
      // Phase 2 will handle scrolling directly to the card.
      if (pendingScrollSignalId === null) {
        // Wait a bit for DOM to update if we just finished loading
        // Use longer delay to account for debounced height measurements and rendering
        if (justFinishedLoading || periodKeyChanged) {
          // Use setTimeout with longer delay to ensure DOM is fully updated and rendered
          // Also wait for the next frame to ensure virtualized items are rendered
          setTimeout(() => {
            // Double-check that the element exists, if not, retry after a short delay
            const sectionId = selectedPeriodKey.replace(/-/g, ' ');
            const targetElement = containerRef.current?.querySelector(
              `[data-section-id="${sectionId}"]`,
            ) as HTMLElement;

            if (targetElement) {
              scrollToSelectedPeriod();
            } else {
              // Retry after another delay if element not found
              setTimeout(() => {
                scrollToSelectedPeriod();
              }, 300);
            }
          }, 300); // Increased delay to allow rendering to complete
        } else {
          scrollToSelectedPeriod();
        }
      }
    } else {
      // Period not found in data yet, but update tracking
      previousSelectedPeriodKeyRef.current = selectedPeriodKey;
    }
  }, [
    selectedPeriodKey,
    isLoadingSelectedPeriod,
    itemHeights,
    data.length,
    data,
    pendingScrollSignalId,
  ]);

  // Helper to get custom week period start (handles YYYY-MM-WN format)
  // Matches the calculation in month-toc.tsx sidebar
  const getCustomWeekPeriodStart = (periodKey: string): moment.Moment => {
    const match = periodKey.match(/^(\d{4})-(\d{2})-W(\d)$/);

    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // moment months are 0-indexed
      const weekOfMonth = parseInt(match[3], 10);
      const weekStartDay = (weekOfMonth - 1) * 7 + 1;

      // Match sidebar calculation: monthStart.clone().date(weekStartDay)
      const monthStart = moment([year, month, 1]).startOf('month');

      return monthStart.clone().date(weekStartDay).startOf('day');
    }

    return moment(periodKey).startOf('day');
  };

  // Helper to get custom week period end (handles YYYY-MM-WN format)
  // Matches the calculation in month-toc.tsx sidebar
  const getCustomWeekPeriodEnd = (periodKey: string): moment.Moment => {
    const match = periodKey.match(/^(\d{4})-(\d{2})-W(\d)$/);

    if (match) {
      const year = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1;
      const weekOfMonth = parseInt(match[3], 10);
      const daysInMonth = moment([year, month, 1]).daysInMonth();
      const weekEndDay = Math.min(weekOfMonth * 7, daysInMonth);

      // Match sidebar calculation: monthStart.clone().date(weekEndDay)
      const monthStart = moment([year, month, 1]).startOf('month');

      return monthStart.clone().date(weekEndDay).endOf('day');
    }

    return moment(periodKey).endOf('day');
  };

  // Helper to get period label based on groupBy
  const getPeriodLabel = (periodKey: string) => {
    if (!periodKey) {
      return '';
    }

    if (groupBy === 'WEEK') {
      // Custom week format: "1 Dec - 7 Dec" or "23 Nov - 30 Nov"
      // Calculation matches sidebar, format matches UI screenshot
      // Show full period range even if it extends into the future
      const weekStart = getCustomWeekPeriodStart(periodKey);
      const weekEnd = getCustomWeekPeriodEnd(periodKey);

      // Format: "D MMM - D MMM" (e.g., "15 Jan - 21 Jan")
      // Always show the full period range, even if it's in the future
      if (weekStart.month() === weekEnd.month()) {
        return `${weekStart.format('D MMM')} - ${weekEnd.format('D MMM')}`;
      }

      return `${weekStart.format('D MMM')} - ${weekEnd.format('D MMM')}`;
    }

    if (groupBy === 'QUARTER') {
      const periodStart = getPeriodStart(periodKey, groupBy);
      const quarterNum = Math.floor(periodStart.month() / 3) + 1;

      return `Q${quarterNum} ${periodStart.format('YYYY')}`;
    }

    // MONTH
    return getPeriodStart(periodKey, groupBy).format('MMM YYYY');
  };

  // Helper to get timeline item date label (for meetings and emails view)
  const getTimelineItemDateLabel = (timeline: any) => {
    const dateStr =
      timeline.startTime || timeline.createdAt || timeline.updatedAt;

    if (!dateStr) {
      return '';
    }

    const date = moment(dateStr);

    if (!date.isValid()) {
      return '';
    }

    return date.format('DD MMM YYYY');
  };

  const handleGroupByChange = useCallback((key: string) => {
    if (key === 'MEETINGS_AND_EMAILS') {
      setIsMeetingsAndEmails(true);
      setGroupBy('MONTH');
      try {
        saveGroupBy('MONTH');
        localStorage.setItem(MEETINGS_AND_EMAILS_FLAG_KEY, 'true');
      } catch {
        // Ignore localStorage errors
      }
    } else if (key === 'WEEK' || key === 'MONTH' || key === 'QUARTER') {
      setIsMeetingsAndEmails(false);
      const newGroupBy = key as GroupByType;

      setGroupBy(newGroupBy);
      saveGroupBy(newGroupBy);
      try {
        localStorage.setItem(MEETINGS_AND_EMAILS_FLAG_KEY, 'false');
      } catch {
        // Ignore localStorage errors
      }
    }
  }, []);

  const groupByMenu = (
    <StyledMenu
      selectedKeys={isMeetingsAndEmails ? ['MEETINGS_AND_EMAILS'] : [groupBy]}
      onClick={({ key }) => handleGroupByChange(key)}
    >
      <Menu.ItemGroup title="Time period">
        <Menu.Item key="WEEK">Week</Menu.Item>
        <Menu.Item key="MONTH">Month</Menu.Item>
        <Menu.Item key="QUARTER">Quarter</Menu.Item>
      </Menu.ItemGroup>
      <Menu.Divider />
      <Menu.ItemGroup title="Interaction">
        <Menu.Item key="MEETINGS_AND_EMAILS">Meetings and emails</Menu.Item>
      </Menu.ItemGroup>
    </StyledMenu>
  );

  const getGroupByLabel = () => {
    if (isMeetingsAndEmails) {
      return 'Meetings and emails';
    }
    switch (groupBy) {
      case 'WEEK':
        return 'Week';
      case 'QUARTER':
        return 'Quarter';
      case 'MONTH':
      default:
        return 'Month';
    }
  };

  const getTocTitle = () => {
    switch (groupBy) {
      case 'WEEK':
        return translate('accounts.page.signals_v2.toc.weeks');
      case 'QUARTER':
        return translate('accounts.page.signals_v2.toc.quarters');
      case 'MONTH':
      default:
        return translate('accounts.page.signals_v2.toc.months');
    }
  };

  // Handle timeline selection from TOC
  const handleTimelineSelect = useCallback(
    (timelineId: string) => {
      if (timelineId === selectedTimelineId) {
        return;
      }

      setSelectedTimelineId(timelineId);
      setIsLoadingTimeline(true);

      // Find the period that contains this timeline
      // The timelineId is in format: source.title + source.id
      for (const period of data) {
        const timeline = period.timelines.find(
          (t) => `${t.source?.title || ''}${t.source?.id || ''}` === timelineId,
        );

        if (timeline) {
          // If the period is not selected, select it first
          if (period.periodKey !== selectedPeriodKey) {
            handlePeriodSelect(period.periodKey);
          }

          // Reset loading after a short delay to allow DOM to update
          setTimeout(() => {
            setIsLoadingTimeline(false);
          }, 100);

          return;
        }
      }

      // Timeline not found, reset loading
      setIsLoadingTimeline(false);
    },
    [selectedTimelineId, data, selectedPeriodKey, handlePeriodSelect],
  );

  // Scroll to selected timeline when it's available
  useEffect(() => {
    // Don't update viewport while loading
    if (isLoadingTimeline || isLoadingSelectedPeriod) {
      previousTimelineLoadingStateRef.current = true;

      return;
    }

    // Track when loading completes
    const justFinishedLoading = previousTimelineLoadingStateRef.current;

    previousTimelineLoadingStateRef.current = false;

    // Don't update if no timeline selected or no data
    if (
      !selectedTimelineId ||
      data.length === 0 ||
      !containerRef.current ||
      !isMeetingsAndEmails
    ) {
      if (selectedTimelineId) {
        previousSelectedTimelineIdRef.current = selectedTimelineId;
      }

      return;
    }

    // Always scroll to selected timeline when:
    // 1. Timeline ID changed (always scroll when selection changes)
    // 2. Just finished loading (only if the selected timeline is in the newly loaded data)
    const timelineIdChanged =
      selectedTimelineId !== previousSelectedTimelineIdRef.current;
    const shouldScroll = justFinishedLoading || timelineIdChanged;

    if (!shouldScroll) {
      return;
    }

    // Reset user scrolling flag when timeline selection changes to allow scrolling
    if (timelineIdChanged) {
      isUserScrollingRef.current = false;
      // Clear any pending scroll timeouts
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    } else if (isUserScrollingRef.current) {
      // Don't scroll if user is actively scrolling and timeline hasn't changed
      return;
    }

    // Function to scroll to the selected timeline
    const scrollToSelectedTimeline = () => {
      if (!containerRef.current) {
        return;
      }

      // Try to find TimelineHeader by data-timeline-id
      // Since we only have data-timeline-id on TimelineHeader now, this will find it
      const targetElement = containerRef.current.querySelector(
        `[data-timeline-id="${selectedTimelineId}"]`,
      ) as HTMLElement;

      if (targetElement) {
        // Use scrollIntoView for more reliable scrolling
        isScrolling.current = true;

        // Calculate position relative to container
        const containerRect = containerRef.current.getBoundingClientRect();
        const elementRect = targetElement.getBoundingClientRect();
        const relativeTop =
          elementRect.top - containerRect.top + containerRef.current.scrollTop;

        // Scroll to position with 200px offset to ensure TimelineHeader is visible
        const scrollOffset = 150;
        const scrollPosition = Math.max(0, relativeTop - scrollOffset);

        containerRef.current.scrollTop = scrollPosition;
        lastScrollTop.current = scrollPosition;

        // Reset scrolling flag after animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isScrolling.current = false;
          });
        });
      }
    };

    // Wait a bit for DOM to update if we just finished loading
    if (justFinishedLoading || timelineIdChanged) {
      // Use setTimeout with delay to ensure DOM is fully updated and rendered
      setTimeout(() => {
        // Double-check that the element exists, if not, retry after a short delay
        // Since we only have data-timeline-id on TimelineHeader now, this will find it
        const targetElement = containerRef.current?.querySelector(
          `[data-timeline-id="${selectedTimelineId}"]`,
        ) as HTMLElement;

        if (targetElement) {
          scrollToSelectedTimeline();
        } else {
          // Retry after another delay if element not found
          setTimeout(() => {
            scrollToSelectedTimeline();
          }, 300);
        }
      }, 300); // Delay to allow rendering to complete
    } else {
      scrollToSelectedTimeline();
    }

    // Update tracking
    previousSelectedTimelineIdRef.current = selectedTimelineId;
  }, [
    selectedTimelineId,
    isLoadingTimeline,
    isLoadingSelectedPeriod,
    data.length,
    data,
    isMeetingsAndEmails,
  ]);

  // Get the first period key for TOC
  const firstPeriodKey = data.length > 0 ? data[0].periodKey : '';

  const hideTocForAccountEmpty =
    hasNoSignalConfigurations ||
    (!isSignalConfigsLoading &&
      signalConfigurations.length > 0 &&
      !isInitializing &&
      !isLoadingSelectedPeriod &&
      !(createdAtFromUrl && !hasJumpedForUrl) &&
      data.length === 0);

  return (
    <>
      {!hideTocForAccountEmpty && isMeetingsAndEmails ? (
        <TocSidebar>
          <MeetingsEmailsTOC
            data={data}
            selectedMonthKey={selectedPeriodKey || firstPeriodKey}
            onMonthSelect={handlePeriodSelect}
            onTitleSelect={handleTimelineSelect}
            isLoadingDown={isLoadingTOC}
            onLoadMore={loadEarlierPeriods}
            signalCountMonthKeys={
              groupBy === 'MONTH' ? monthKeysWithData : undefined
            }
          />
        </TocSidebar>
      ) : !hideTocForAccountEmpty &&
        (groupBy === 'MONTH' || groupBy === 'WEEK' || groupBy === 'QUARTER') ? (
        <TocSidebar>
          <MonthTOC
            groupBy={groupBy}
            onMonthSelect={handlePeriodSelect}
            selectedMonthKey={selectedPeriodKey || firstPeriodKey}
            billingStartDate={billingStartDate}
            useCustomWeeks={groupBy === 'WEEK'}
            title={getTocTitle()}
            allowedPeriodKeys={
              groupBy === 'MONTH' ? monthKeysWithData : undefined
            }
          />
        </TocSidebar>
      ) : null}
      <TimelineWrapper ref={containerRef}>
        {!isLoadingSelectedPeriod &&
          !hideTocForAccountEmpty &&
          !isSignalConfigsLoading && (
            <HeaderSection>
              <Dropdown
                trigger={['click']}
                overlay={groupByMenu}
                overlayStyle={{
                  minWidth: GROUP_BY_MENU_WIDTH_PX,
                  width: GROUP_BY_MENU_WIDTH_PX,
                }}
              >
                <GroupByDropdownTrigger>
                  <GroupByLabel>Group by: {getGroupByLabel()}</GroupByLabel>
                  <FontAwesomeIcon icon={faChevronDown} />
                </GroupByDropdownTrigger>
              </Dropdown>
            </HeaderSection>
          )}
        <TimelineContainer key={`${groupBy}-${isMeetingsAndEmails}`}>
          {isSignalConfigsLoading && (
            <FullPageLoader>
              <Loading size="large" />
            </FullPageLoader>
          )}
          {!isSignalConfigsLoading && hasNoSignalConfigurations && (
            <SignalsV2AccountEmptyState
              kind="no_configs"
              canCreateSignal={canCreateSignal}
            />
          )}
          {!isSignalConfigsLoading && !hasNoSignalConfigurations && (
            <>
              {(isLoadingSelectedPeriod ||
                isLoadingTimeline ||
                isInitializing ||
                (createdAtFromUrl && !hasJumpedForUrl)) && (
                <FullPageLoader>
                  <Loading size="large" />
                </FullPageLoader>
              )}
              {!isLoadingSelectedPeriod &&
                !isInitializing &&
                !(createdAtFromUrl && !hasJumpedForUrl) && (
                  <>
                    {data.length === 0 ? (
                      <SignalsV2AccountEmptyState
                        kind="no_signals"
                        canCreateSignal={false}
                      />
                    ) : (
                      <TimelineContent
                        key={`${groupBy}-${isMeetingsAndEmails}`}
                      >
                        {/* Top loading indicator for scroll up */}
                        {isLoadingUp && (
                          <TopLoadingIndicator>
                            <Loading size="small" />
                          </TopLoadingIndicator>
                        )}

                        {/* Top sentinel for Intersection Observer */}
                        <Sentinel ref={topSentinelRef} />

                        {/* Top spacer for items before visible range */}
                        {topSpacerHeight > 0 && (
                          <Spacer height={topSpacerHeight} />
                        )}

                        {/* Render only visible items */}
                        {data
                          .slice(visibleRange.start, visibleRange.end)
                          .map((item, relativeIndex) => {
                            const absoluteIndex =
                              visibleRange.start + relativeIndex;
                            const periodLabel = getPeriodLabel(item.periodKey);

                            // Sort timelines by date (latest first) when meetings and emails is selected
                            const sortedTimelines = isMeetingsAndEmails
                              ? [...item.timelines].sort((a, b) => {
                                  const dateA = moment(
                                    a.startTime || a.createdAt || a.updatedAt,
                                  );
                                  const dateB = moment(
                                    b.startTime || b.createdAt || b.updatedAt,
                                  );

                                  if (!dateA.isValid() || !dateB.isValid()) {
                                    return 0;
                                  }

                                  return dateB.valueOf() - dateA.valueOf();
                                })
                              : item.timelines;

                            return (
                              <PeriodSection
                                key={item.periodKey || absoluteIndex}
                                data-section-id={
                                  item.periodKey
                                    ? item.periodKey.replace(/-/g, ' ')
                                    : undefined
                                }
                                ref={(el) => {
                                  if (el) {
                                    itemRefs.current.set(absoluteIndex, el);
                                  } else {
                                    itemRefs.current.delete(absoluteIndex);
                                  }
                                }}
                              >
                                {!isMeetingsAndEmails && (
                                  <PeriodLabelColumn>
                                    <PeriodLabelLeft>
                                      {periodLabel}
                                    </PeriodLabelLeft>
                                  </PeriodLabelColumn>
                                )}
                                {sortedTimelines.length === 0 &&
                                !isMeetingsAndEmails ? (
                                  <PeriodContentColumn>
                                    <PeriodEmptyState>
                                      {translate(
                                        'accounts.page.signals_v2.empty_state.no_signals_detected',
                                      )}
                                    </PeriodEmptyState>
                                  </PeriodContentColumn>
                                ) : sortedTimelines.length === 0 &&
                                  isMeetingsAndEmails ? (
                                  <>
                                    <PeriodLabelColumn>
                                      <PeriodLabelLeft>
                                        {periodLabel}
                                      </PeriodLabelLeft>
                                    </PeriodLabelColumn>
                                    <PeriodContentColumn>
                                      <PeriodEmptyState>
                                        {translate(
                                          'accounts.page.signals_v2.empty_state.no_signals_detected',
                                        )}
                                      </PeriodEmptyState>
                                    </PeriodContentColumn>
                                  </>
                                ) : (
                                  <PeriodContentColumn
                                    $meetingsEmailsWithItems={
                                      isMeetingsAndEmails &&
                                      sortedTimelines.length > 0
                                    }
                                  >
                                    <TimelineItemsContainer
                                      $isMeetingsAndEmails={isMeetingsAndEmails}
                                    >
                                      {sortedTimelines
                                        .filter(
                                          (timeline) =>
                                            timeline.source?.title != null,
                                        )
                                        .map((timeline) => {
                                          const hasSignalOccurrences =
                                            timeline.signalOccurrences !=
                                              null &&
                                            timeline.signalOccurrences.length >
                                              0;

                                          // Only render if there's content to show
                                          if (
                                            !hasSignalOccurrences &&
                                            !isMeetingsAndEmails
                                          ) {
                                            return (
                                              <PeriodEmptyState
                                                key={timeline.source.id}
                                              >
                                                {translate(
                                                  'accounts.page.signals_v2.empty_state.no_data_available',
                                                )}
                                              </PeriodEmptyState>
                                            );
                                          }

                                          return (
                                            <TimelineItem
                                              key={timeline.source.id}
                                              $isMeetingsAndEmails={
                                                isMeetingsAndEmails
                                              }
                                              data-timeline-id={
                                                isMeetingsAndEmails
                                                  ? `${timeline.source?.title || ''}${timeline.source?.id || ''}`
                                                  : undefined
                                              }
                                            >
                                              {isMeetingsAndEmails && (
                                                <TimelineItemDateColumn>
                                                  <TimelineItemDateLabel>
                                                    <div>
                                                      {getTimelineItemDateLabel(
                                                        timeline,
                                                      )}
                                                    </div>
                                                    {timeline.startTime && (
                                                      <div>
                                                        {' '}
                                                        {moment(
                                                          timeline.startTime,
                                                        ).format('HH:mm')}
                                                      </div>
                                                    )}
                                                  </TimelineItemDateLabel>
                                                </TimelineItemDateColumn>
                                              )}
                                              <TimelineItemContentColumn
                                                $constrainReadingWidth={
                                                  isMeetingsAndEmails
                                                }
                                              >
                                                {/* Show timeline header if source title exists - only in meetings & emails view */}
                                                {isMeetingsAndEmails &&
                                                  timeline.source?.title && (
                                                    <TimelineHeader
                                                      data-timeline-id={`${timeline.source?.title || ''}${timeline.source?.id || ''}`}
                                                    >
                                                      <FlexWrapperWithShortGapBaseline>
                                                        <KryptonMenuIcon
                                                          icon={
                                                            timeline.source
                                                              .type === 'EMAIL'
                                                              ? faEnvelope
                                                              : faCalendar
                                                          }
                                                          color={
                                                            timeline.source
                                                              .type === 'EMAIL'
                                                              ? 'magenta'
                                                              : 'blue'
                                                          }
                                                        />
                                                        <ProductiveHeading03>
                                                          {
                                                            timeline.source
                                                              .title
                                                          }
                                                        </ProductiveHeading03>
                                                      </FlexWrapperWithShortGapBaseline>
                                                    </TimelineHeader>
                                                  )}
                                                {/* Show recap before signals if it exists - only in meetings & emails view */}
                                                {isMeetingsAndEmails &&
                                                  (timeline.summaryAvailable ||
                                                    timeline.signalAwareRecap ||
                                                    timeline.summary) && (
                                                    <RecapSection>
                                                      <RecapTitle>
                                                        <FontAwesomeIcon
                                                          icon={faAlignLeft}
                                                        />
                                                        <ProductiveHeading01>
                                                          {timeline.source
                                                            .type === 'EMAIL'
                                                            ? translate(
                                                                'accounts.page.timeline.email_recap',
                                                              )
                                                            : translate(
                                                                'accounts.page.timeline.meeting_recap',
                                                              )}
                                                        </ProductiveHeading01>
                                                      </RecapTitle>
                                                      <RecapText>
                                                        {timeline.signalAwareRecap ? (
                                                          <Fragment>
                                                            {
                                                              timeline.signalAwareRecap
                                                            }
                                                          </Fragment>
                                                        ) : (
                                                          timeline.summary
                                                        )}
                                                      </RecapText>
                                                    </RecapSection>
                                                  )}
                                                {hasSignalOccurrences && (
                                                  <SignalCardsContainer>
                                                    {timeline.signalOccurrences.map(
                                                      (signal, signalIndex) => {
                                                        const reason =
                                                          signal.fields?.find(
                                                            (field) =>
                                                              Object.keys(
                                                                signal.metaData,
                                                              )[0] ===
                                                              String(
                                                                field.fieldId,
                                                              ),
                                                          );

                                                        return (
                                                          <TimelineSignalCard
                                                            key={
                                                              signal.signalOccurrenceId
                                                            }
                                                            title={signal.title}
                                                            description={
                                                              signal.summary
                                                            }
                                                            people={
                                                              signal.reportedByUsers ??
                                                              []
                                                            }
                                                            reason={reason}
                                                            signalType={
                                                              (signal?.signalType as SignalType) ||
                                                              SignalType.Risk
                                                            }
                                                            signalName={
                                                              signal?.signalName ||
                                                              ''
                                                            }
                                                            createdAt={
                                                              signal.createdAt
                                                            }
                                                            citations={
                                                              signal.citationMapping
                                                            }
                                                            isFirst={
                                                              signalIndex === 0
                                                            }
                                                            isMeetingsAndEmails={
                                                              isMeetingsAndEmails
                                                            }
                                                            projectId={
                                                              projectId
                                                            }
                                                            signalOccurrenceId={
                                                              signal.signalOccurrenceId
                                                            }
                                                            isHighlighted={
                                                              highlightedSignalOccurrenceId ===
                                                              signal.signalOccurrenceId
                                                            }
                                                          />
                                                        );
                                                      },
                                                    )}
                                                  </SignalCardsContainer>
                                                )}
                                              </TimelineItemContentColumn>
                                            </TimelineItem>
                                          );
                                        })
                                        .filter(Boolean)}
                                    </TimelineItemsContainer>
                                  </PeriodContentColumn>
                                )}
                              </PeriodSection>
                            );
                          })}

                        {/* Bottom spacer for items after visible range */}
                        {bottomSpacerHeight > 0 && (
                          <Spacer height={bottomSpacerHeight} />
                        )}

                        {/* Bottom sentinel for Intersection Observer */}
                        <Sentinel ref={bottomSentinelRef} />

                        {/* Bottom loading indicator for scroll down */}
                        {isLoadingDown && (
                          <BottomLoadingIndicator>
                            <Loading size="small" />
                          </BottomLoadingIndicator>
                        )}
                      </TimelineContent>
                    )}
                  </>
                )}
            </>
          )}
        </TimelineContainer>
      </TimelineWrapper>
    </>
  );
};
